import React from 'react';
import {
	Alert, Badge, Button, ButtonGroup, Input, Modal, ModalBody, ModalFooter, ModalHeader, Table, UncontrolledTooltip
} from 'reactstrap';
import { auth } from '../../Api';
import { formatServerMessage } from '../../utils/serverErrors';
import {
	invoiceStatus, statusHover, remindersLine, estimateText, billingLine, contactLine, canSeeBilling, noteProblem, downloadPdf, monthLabel, olderYears, visibleInvoices, RECENT
} from '../../utils/invoices';
import RowMenu from './row-menu';
import EditBillingDialog from '../billing-overview/edit-billing-dialog';
import './billing-tab.scss';
import { isPrivateAccount } from '../../utils/accountType';

// The Account page's Billing tab (laravel BillingController): a line saying from when and at what price the
// company is billed and who invoices are addressed to (the super admin gets an Edit link: the Billing page's Edit
// billing dialog), this month so far and an estimate on one line, and the invoices, one slim row each: unpaid ones pinned at the top, then the last 12 months (a year picker shows
// older ones). Pay now, Download PDF, Download receipt and Resend; invoices are paid online only. A company admin
// sees their own company (the server decides which); the super admin sees the company chosen in the admin
// website (localStorage companyId), a void invoice's void type and reason (on hover) and a link to a payment
// in the Stripe Dashboard, and can void an unpaid invoice: void and reissue (a corrected invoice at once) or void
// and waive. Resellers and district managers do not see billing. Tooltips use the Mobiliis style
// (billing-tab.scss); the Actions menu is drawn outside the card (row-menu.js).
class BillingTab extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			error: '',
			notice: '',
			data: null,
			busy: null,
			view: RECENT,
			// { kind: 'reissue' | 'waive', invoice, preview, text, problem, starts (the invoice's duty starts),
			//   removed: {id: true} (disputed ones left off), removeText (why) }
			dialog: null,
			// The super admin's Edit billing dialog is open
			editing: false
		};
		this.load = this.load.bind(this);
		this.onVisible = this.onVisible.bind(this);
		// When the data was last asked for (ms), whether Pay was pressed, and the second look after a payment
		this.loadedAt = 0;
		this.payPressed = false;
		this.recheck = null;
	}

	componentDidMount() {
		this.load();
		document.addEventListener('visibilitychange', this.onVisible);
	}

	componentWillUnmount() {
		document.removeEventListener('visibilitychange', this.onVisible);
		clearTimeout(this.recheck);
		clearTimeout(this.previewTimer);
	}

	// Back on this browser tab (after paying in the tab Pay opened): the invoices again, quietly. Only when there is an
	// unpaid invoice or Pay was pressed, not while a dialog is open, and not more than once in 5 seconds. After a payment, one more look a few
	// seconds later, in case Stripe's confirmation reaches the server just after.
	onVisible() {
		if (document.visibilityState !== 'visible' || !this.state.data || this.state.loading || this.state.dialog || this.state.editing) {
			return;
		}
		const unpaid = (this.state.data.invoices || []).some((invoice) => invoice.status === 'unpaid');
		if (!unpaid && !this.payPressed) {
			return;
		}
		if (Date.now() - this.loadedAt < 5000) {
			return;
		}
		this.load(true);
		if (this.payPressed) {
			this.payPressed = false;
			clearTimeout(this.recheck);
			this.recheck = setTimeout(() => this.load(true), 6000);
		}
	}

	level() {
		try {
			return localStorage.getItem('admin_level');
		} catch (e) {
			return null;
		}
	}

	// quiet: a reload in the background (back on this browser tab): what is shown stays until the new data is there,
	// and a failure changes nothing
	load(quiet) {
		const level = this.level();
		if (!canSeeBilling(level)) {
			this.setState({ loading: false, error: 'Billing is shown to the company admin and to Mobiliis only.' });
			return;
		}
		const request = {};
		if (level === 'a') {
			request.company_id = localStorage.getItem('companyId');
		}
		this.loadedAt = Date.now();
		if (quiet !== true) {
			this.setState({ loading: true, error: '' });
		}
		auth.getBillingOverview(request)
			.then((res) => {
				if (res.data.code == 200) {
					this.setState({ loading: false, data: res.data });
				} else if (quiet !== true) {
					this.setState({ loading: false, error: formatServerMessage(res.data.message, 'Billing could not be loaded.') });
				}
			})
			.catch(() => {
				if (quiet !== true) {
					this.setState({ loading: false, error: 'Billing could not be loaded. Please try again.' });
				}
			});
	}

	// Puts an invoice the server sent back in place of the old one
	replaceInvoice(invoice) {
		if (!invoice) {
			return;
		}
		this.setState((state) => ({
			data: { ...state.data, invoices: state.data.invoices.map((row) => (row.id === invoice.id ? invoice : row)) }
		}));
	}

	download(invoice, receipt) {
		const busy = (receipt ? 'receipt' : 'pdf') + invoice.id;
		this.setState({ busy, notice: '', error: '' });
		(receipt ? auth.getReceiptPdf({ id: invoice.id }) : auth.getInvoicePdf({ id: invoice.id }))
			.then((res) => {
				if (res.data.code == 200) {
					downloadPdf(res.data.file_name, res.data.pdf_base64);
					this.setState({ busy: null });
				} else {
					this.setState({ busy: null, error: formatServerMessage(res.data.message, 'The PDF could not be made.') });
				}
			})
			.catch(() => this.setState({ busy: null, error: 'The PDF could not be made. Please try again.' }));
	}

	resend(invoice) {
		this.setState({ busy: 'resend' + invoice.id, notice: '', error: '' });
		auth.resendInvoice({ id: invoice.id })
			.then((res) => {
				if (res.data.code == 200) {
					this.replaceInvoice(res.data.invoice);
					this.setState({ busy: null, notice: formatServerMessage(res.data.message, 'The invoice has been emailed.') });
				} else {
					this.setState({ busy: null, error: formatServerMessage(res.data.message, 'The invoice could not be emailed.') });
				}
			})
			.catch(() => this.setState({ busy: null, error: 'The invoice could not be emailed. Please try again.' }));
	}

	pay(invoice) {
		this.payPressed = true;
		window.open(invoice.pay_url, '_blank', 'noopener');
	}

	// Opens the void dialog and asks the server what the void would do (it changes nothing)
	openDialog(kind, invoice) {
		this.setState({ dialog: { kind, invoice, preview: null, text: '', problem: null, starts: null, removed: {}, removeText: '' }, notice: '', error: '' });
		this.askPreview(invoice, []);
	}

	// What the void would do, with the disputed duty starts left off (the server changes nothing)
	askPreview(invoice, removeIds) {
		auth.voidInvoicePreview({ id: invoice.id, remove_ids: removeIds })
			.then((res) => {
				const dialog = this.state.dialog;
				// Only the answer for the duty starts ticked now
				if (!dialog || dialog.invoice.id !== invoice.id || Object.keys(dialog.removed).join(',') !== removeIds.join(',')) {
					return;
				}
				if (res.data.code == 200) {
					this.setState({ dialog: { ...dialog, preview: res.data, starts: dialog.starts || res.data.starts } });
				} else {
					this.setState({ dialog: { ...dialog, problem: formatServerMessage(res.data.message, 'What the void would do could not be worked out.') } });
				}
			})
			.catch(() => this.state.dialog && this.setState({ dialog: { ...this.state.dialog, problem: 'What the void would do could not be worked out. Please try again.' } }));
	}

	// Ticked: on the corrected invoice; unticked: a disputed duty start, left off and never charged
	toggleStart(id) {
		const dialog = this.state.dialog;
		const removed = { ...dialog.removed };
		if (removed[id]) {
			delete removed[id];
		} else {
			removed[id] = true;
		}
		this.setState({ dialog: { ...dialog, removed, preview: { ...dialog.preview, pending: true }, problem: null } });
		clearTimeout(this.previewTimer);
		this.previewTimer = setTimeout(() => this.askPreview(dialog.invoice, Object.keys(removed)), 400);
	}

	submitDialog() {
		const { dialog } = this.state;
		const removeIds = Object.keys(dialog.removed || {});
		const problem = noteProblem(dialog.text, 'why it is voided')
			|| (dialog.kind === 'reissue' && removeIds.length > 0 ? noteProblem(dialog.removeText, 'why these duty starts are removed') : null);
		if (problem) {
			this.setState({ dialog: { ...dialog, problem } });
			return;
		}
		const request = { id: dialog.invoice.id, reason: dialog.text.trim() };
		if (dialog.kind === 'reissue' && removeIds.length > 0) {
			request.remove_ids = removeIds;
			request.remove_reason = dialog.removeText.trim();
		}
		const call = dialog.kind === 'reissue' ? auth.voidAndReissueInvoice(request) : auth.voidAndWaiveInvoice(request);
		this.setState({ busy: 'dialog' });
		call.then((res) => {
			if (res.data.code == 200) {
				this.setState({ busy: null, dialog: null, notice: formatServerMessage(res.data.message, 'Saved.') });
				this.load();
			} else {
				this.setState({ busy: null, dialog: { ...dialog, problem: formatServerMessage(res.data.message, 'It could not be saved.') } });
			}
		}).catch(() => this.setState({ busy: null, dialog: { ...dialog, problem: 'It could not be saved. Please try again.' } }));
	}

	// Shown only once billing has started (the line at the top says when it has not)
	renderThisMonth(month) {
		const estimate = month.estimate;
		return (
			<div>
				<div className="d-flex flex-wrap align-items-baseline">
					<span className="mr-4">
						<span className="text-muted">So far this month:</span> <strong>USD {month.usd}</strong>
						<small className="text-muted"> ({month.duty_starts} duty {month.duty_starts === 1 ? 'start' : 'starts'})</small>
					</span>
					<span>
						<span className="text-muted">Expected by the end of the month:</span> <strong>about USD {estimate.usd}</strong>
						<small className="text-muted"> ({estimate.duty_starts} duty {estimate.duty_starts === 1 ? 'start' : 'starts'})</small>
						<i className="fa fa-info-circle text-muted ml-2" id="billing-estimate-info" style={{ cursor: 'help' }} aria-label="How the estimate is made" />
						<UncontrolledTooltip target="billing-estimate-info" placement="bottom" className="mobiliis-tooltip">
							{estimateText(month)}
						</UncontrolledTooltip>
					</span>
				</div>
				{month.carried.duty_starts > 0 && (
					<small className="d-block text-muted">
						Not invoiced yet from earlier months: {month.carried.duty_starts} duty {month.carried.duty_starts === 1 ? 'start' : 'starts'},
						USD {month.carried.usd} (they go on the next invoice).
					</small>
				)}
			</div>
		);
	}

	renderRow(invoice, canManage, onlinePayment) {
		const { busy } = this.state;
		const status = invoiceStatus(invoice);
		const unpaid = invoice.status === 'unpaid';
		// "Paid on <date>, online payment"; a void invoice's type and reason for the super admin only
		const hover = statusHover(invoice, canManage);
		const statusId = 'billing-status-' + invoice.id;
		const items = [
			{ label: 'Download PDF', disabled: busy === 'pdf' + invoice.id, onClick: () => this.download(invoice, false) }
		];
		if (invoice.status === 'paid') {
			items.push({ label: 'Download receipt', disabled: busy === 'receipt' + invoice.id, onClick: () => this.download(invoice, true) });
		}
		if (invoice.status !== 'void') {
			items.push({ label: 'Resend', disabled: busy === 'resend' + invoice.id, onClick: () => this.resend(invoice) });
		}
		if (canManage && invoice.stripe_url) {
			items.push({ divider: true }, { header: 'Super admin' },
				{ label: 'View payment in Stripe', onClick: () => window.open(invoice.stripe_url, '_blank', 'noopener') });
		}
		if (canManage && unpaid) {
			items.push({ divider: true }, { header: 'Super admin' },
				{ label: 'Void and reissue...', onClick: () => this.openDialog('reissue', invoice) },
				{ label: 'Void and waive...', danger: true, onClick: () => this.openDialog('waive', invoice) });
		}
		return (
			<tr key={invoice.id} className={unpaid ? (invoice.overdue ? 'table-danger' : 'table-warning') : ''}>
				<td className="align-middle text-nowrap">
					{invoice.number}
					{invoice.replaces && <small className="text-muted"> · replaces {invoice.replaces}</small>}
					{invoice.replaced_by && <small className="text-muted"> · replaced by {invoice.replaced_by}</small>}
				</td>
				<td className="align-middle">{invoice.period_label}</td>
				<td className="align-middle text-right">{invoice.total_usd}</td>
				<td className="align-middle">
					<Badge color={status.tone} id={statusId} style={hover ? { cursor: 'help' } : undefined}>{status.label}</Badge>
					{hover && (
						<UncontrolledTooltip target={statusId} placement="right" className="mobiliis-tooltip">{hover}</UncontrolledTooltip>
					)}
					{/* The super admin: the reminders sent for it (App\Overdue) */}
					{canManage && remindersLine(invoice) && <small className="text-muted ml-2">{remindersLine(invoice)}</small>}
				</td>
				<td className="align-middle text-right text-nowrap">
					<ButtonGroup size="sm">
						{unpaid && invoice.pay_url && (
							<Button color="primary" onClick={() => this.pay(invoice)}
								title={onlinePayment ? 'Opens the secure online payment page' : 'Online payment is not available yet'}>Pay now</Button>
						)}
						<RowMenu items={items} />
					</ButtonGroup>
				</td>
			</tr>
		);
	}

	renderInvoices(data) {
		const { view } = this.state;
		const invoices = data.invoices;
		if (invoices.length === 0) {
			return <p className="mb-0">No invoices yet. Invoices are made on the 1st of each month for the month before.</p>;
		}
		const { pinned, rest } = visibleInvoices(invoices, view, data.this_month.month);
		return (
			<React.Fragment>
				<Table size="sm" hover responsive className="mb-1">
					<thead>
						<tr>
							<th>Invoice</th>
							<th>Month</th>
							<th className="text-right">USD</th>
							<th>Status</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{pinned.map((invoice) => this.renderRow(invoice, data.can_manage, data.online_payment))}
						{rest.map((invoice) => this.renderRow(invoice, data.can_manage, data.online_payment))}
					</tbody>
				</Table>
				{pinned.length + rest.length === 0 && (
					<p className="text-muted small mb-0">{view === RECENT ? 'No invoices in the last 12 months.' : 'No invoices in ' + view + '.'}</p>
				)}
			</React.Fragment>
		);
	}

	renderYearPicker(data) {
		const years = olderYears(data.invoices, data.this_month.month);
		if (years.length === 0) {
			return null;
		}
		return (
			<Input type="select" bsSize="sm" style={{ width: 'auto' }} value={this.state.view} aria-label="Which invoices"
				onChange={(e) => this.setState({ view: e.target.value })}>
				<option value={RECENT}>Last 12 months</option>
				{years.map((year) => <option key={year} value={year}>{year}</option>)}
			</Input>
		);
	}

	renderPreview(dialog) {
		const preview = dialog.preview;
		const invoice = dialog.invoice;
		if (!preview) {
			return dialog.problem ? null : <p className="text-muted">Working out what will happen...</p>;
		}
		if (dialog.kind === 'waive') {
			const waive = preview.waive;
			return (
				<p>
					Invoice {invoice.number} is voided and its {waive.duty_starts} duty {waive.duty_starts === 1 ? 'start' : 'starts'} (USD {waive.total_usd})
					are <strong>never charged</strong>: they will not go on any later invoice. Nothing is due on it and its pay link stops working.
					It keeps its number and shows VOID. This cannot be undone.
				</p>
			);
		}
		const reissue = preview.reissue;
		const month = invoice.period_label;
		const removedCount = Object.keys(dialog.removed || {}).length;
		let outcome;
		if (reissue.result === 'reissued') {
			outcome = (
				<li>
					At once, a <strong>corrected invoice</strong> for {month} is made with a new number and emailed to the company admin:
					{' '}{reissue.duty_starts} duty {reissue.duty_starts === 1 ? 'start' : 'starts'}, <strong>USD {reissue.total_usd}</strong>,
					at the price and billing start date ({reissue.billing_starts_on}) set now. It says it replaces {invoice.number}.
					<Table size="sm" className="mt-2 mb-0 small">
						<tbody>
							{reissue.lines.map((line, index) => (
								<tr key={index}>
									<td>{line.label}</td>
									<td className="text-right">{line.duty_starts} x {line.rate}</td>
									<td className="text-right">{line.amount}</td>
								</tr>
							))}
						</tbody>
					</Table>
				</li>
			);
		} else if (reissue.result === 'under_minimum') {
			outcome = (
				<li>
					The corrected total would be USD {reissue.total_usd}, <strong>under the minimum of USD {reissue.minimum_usd}</strong>: no new invoice
					is made. The duty starts go on the company's next invoice.
				</li>
			);
		} else if (reissue.result === 'not_billed') {
			outcome = <li>The company has <strong>no billing start date</strong> now: no new invoice is made.</li>;
		} else {
			outcome = (
				<li>
					With the price and billing start date set now there is <strong>nothing to invoice</strong> for {month}: no new invoice is made.
				</li>
			);
		}
		return (
			<React.Fragment>
				<p className="mb-2">Correct the company's price or billing start date first (Edit, at the top of this tab). Then:</p>
				<ul className="pl-3">
					<li>Invoice {invoice.number} (USD {invoice.total_usd}) is voided. It keeps its number and shows VOID; its pay link stops working.</li>
					{removedCount > 0 && (
						<li>
							<strong>{removedCount} disputed duty {removedCount === 1 ? 'start is' : 'starts are'} left off</strong> the corrected invoice and
							never charged (waived, with your reason).
						</li>
					)}
					{preview.pending ? <li className="text-muted">Working out the corrected invoice...</li> : outcome}
					{/* The later duty starts priced again, named by their months (the server's sentence) */}
					<li>{reissue.later_text}</li>
				</ul>
			</React.Fragment>
		);
	}

	// The invoice's duty starts: untick disputed ones (left off the corrected invoice, never charged)
	renderStarts(dialog) {
		const starts = dialog.starts;
		if (!starts || starts.length === 0) {
			return null;
		}
		const removed = dialog.removed || {};
		const removedCount = Object.keys(removed).length;
		return (
			<div className="mt-3">
				<div className="small mb-1">
					<strong>Disputed duty starts?</strong> Untick them: they are left off the corrected invoice and never charged.
					<span className="text-muted"> {starts.length - removedCount} of {starts.length} kept.</span>
				</div>
				<div className="billing-starts-list border rounded">
					<Table size="sm" className="mb-0 small">
						<thead>
							<tr><th></th><th>Guard</th><th>Client</th><th>Start (company time)</th></tr>
						</thead>
						<tbody>
							{starts.map((start) => (
								<tr key={start.id} className={removed[start.id] ? 'text-muted billing-start-removed' : ''}>
									<td>
										<Input type="checkbox" className="position-static m-0" checked={!removed[start.id]}
											aria-label={'Keep the duty start of ' + start.guard + ', ' + start.at} onChange={() => this.toggleStart(start.id)} />
									</td>
									<td>{start.guard}</td>
									<td>{start.client}</td>
									<td className="text-nowrap">{start.at}</td>
								</tr>
							))}
						</tbody>
					</Table>
				</div>
				{removedCount > 0 && (
					<Input type="textarea" rows="2" maxLength={500} className="mt-2" value={dialog.removeText}
						placeholder="Why these duty starts are removed (required, kept with them)"
						onChange={(e) => this.setState({ dialog: { ...dialog, removeText: e.target.value, problem: null } })} />
				)}
			</div>
		);
	}

	renderDialog() {
		const { dialog, busy } = this.state;
		if (!dialog) {
			return null;
		}
		const reissue = dialog.kind === 'reissue';
		const makesInvoice = reissue && dialog.preview && dialog.preview.reissue.result === 'reissued';
		const close = () => this.setState({ dialog: null });
		return (
			<Modal isOpen={true} toggle={close} size={reissue ? 'lg' : undefined}>
				<ModalHeader toggle={close}>{reissue ? 'Void and reissue' : 'Void and waive'} {dialog.invoice.number}</ModalHeader>
				<ModalBody>
					{this.renderPreview(dialog)}
					<Input type="textarea" rows="2" maxLength={500} value={dialog.text} placeholder="Reason (required, kept with the void invoice)"
						onChange={(e) => this.setState({ dialog: { ...dialog, text: e.target.value, problem: null } })} />
					{reissue && this.renderStarts(dialog)}
					{dialog.problem && <div className="text-danger mt-2">{dialog.problem}</div>}
				</ModalBody>
				<ModalFooter>
					<Button color={reissue ? 'primary' : 'danger'} disabled={busy === 'dialog' || !dialog.preview || !!dialog.preview.pending} onClick={() => this.submitDialog()}>
						{reissue ? (makesInvoice || !dialog.preview ? 'Void and reissue' : 'Void without a new invoice') : 'Void and waive'}
					</Button>
					<Button color="secondary" onClick={close}>Cancel</Button>
				</ModalFooter>
			</Modal>
		);
	}

	render() {
		const { loading, error, notice, data } = this.state;
		if (loading && !data) {
			return <p className="mt-3">Loading billing...</p>;
		}
		return (
			<div className="mt-3">
				{error && <Alert color="danger">{error}</Alert>}
				{notice && <Alert color="success">{notice}</Alert>}
				{data && (
					<React.Fragment>
						{data.can_manage && <p className="text-muted small mb-2">Billing of {data.company.name} (as the super admin).</p>}
						<p className="mb-0">
							{billingLine(data.billing, isPrivateAccount())}
							{/* The super admin changes the price and start date (the Billing page's Edit billing dialog) */}
							{data.can_manage && (
								<Button color="link" size="sm" className="p-0 ml-2 align-baseline" onClick={() => this.setState({ editing: true, notice: '' })}>Edit</Button>
							)}
						</p>
						<p className="text-muted small mb-3">{contactLine(data.billing, isPrivateAccount())}</p>
						{data.this_month.billed && (
							<React.Fragment>
								<h5 className="mb-1">This month ({monthLabel(data.this_month.month)})</h5>
								<div className="mb-3">{this.renderThisMonth(data.this_month)}</div>
							</React.Fragment>
						)}
						<div className="d-flex align-items-center justify-content-between mb-1">
							<h5 className="mb-0">Invoices</h5>
							{this.renderYearPicker(data)}
						</div>
						<p className="text-muted small mb-1">
							Emailed to the company admin on the 1st of each month for the month before; paid online.
							{!data.online_payment && ' Online payment is not available yet.'}
						</p>
						{this.renderInvoices(data)}
					</React.Fragment>
				)}
				{this.renderDialog()}
				{this.state.editing && data && (
					<EditBillingDialog
						companies={[{ id: data.company.id, name: data.company.name, company_rate_usd: data.billing.company_rate_usd, starts_on: data.billing.starts_on }]}
						defaultRate={data.billing.default_rate_usd}
						onClose={() => this.setState({ editing: false })}
						onSaved={(message) => { this.setState({ editing: false, notice: message }); this.load(); }} />
				)}
			</div>
		);
	}
}

export default BillingTab;
